import type {
  StaffSessionResponse,
  UserSearchResult,
  UserAccount,
} from '../types/staff'
import { fetchOptions, fetchWithCSRFRetry } from './csrf'

export const staffApi = {
  // Get current staff session
  async getSession(): Promise<StaffSessionResponse> {
    const response = await fetch('/api/v1/staff/session', fetchOptions())
    if (!response.ok) {
      return { authenticated: false }
    }
    return response.json()
  },

  // Search users for impersonation
  async searchUsers(query: string): Promise<UserSearchResult[]> {
    const response = await fetch(`/api/v1/staff/users?q=${encodeURIComponent(query)}`, fetchOptions())
    if (!response.ok) {
      return []
    }
    const data = await response.json()
    return data.users
  },

  // Get user's accounts
  async getUserAccounts(userId: string): Promise<UserAccount[]> {
    const response = await fetch(`/api/v1/staff/users/${userId}`, fetchOptions())
    if (!response.ok) {
      return []
    }
    const data = await response.json()
    return data.accounts
  },

  // Start impersonation
  async startImpersonation(userId: string, accountId: string, reason?: string): Promise<{ success: boolean; error?: string }> {
    const response = await fetchWithCSRFRetry('/admin/impersonation', {
      ...fetchOptions('POST'),
      body: JSON.stringify({
        user_id: userId,
        account_id: accountId,
        reason: reason || '',
      }),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      return { success: false, error: data.error || 'Failed to start impersonation' }
    }

    return { success: true }
  },

  // End impersonation
  async endImpersonation(): Promise<{ success: boolean }> {
    const response = await fetchWithCSRFRetry('/admin/impersonation', fetchOptions('DELETE'))
    return { success: response.ok }
  },

  // Logout staff
  async logout(): Promise<{ success: boolean }> {
    const response = await fetchWithCSRFRetry('/admin/sign_out', fetchOptions('DELETE'))
    return { success: response.ok }
  },
}
